import { useRef, useState } from 'react';

import { IconDots } from '@tabler/icons-react';

import {
  BaseButton,
  Button,
  ButtonProps,
  Popover,
  PopoverPanelProps,
  PopoverRootProps,
} from '@cfa/react-core';

import { useClickOutside } from '@/hooks/useClickOutside';

import { cn } from '@/utils/tailwind';

interface PopoverMenuButtonProps extends PopoverRootProps {
  handleClick?: () => void;
  hideMenu?: boolean;
  size?: ButtonProps['size'];
  text?: string | React.ReactNode;
  placement?: PopoverPanelProps['placement'];
  className?: string;
}

const PopoverMenuButton: React.FC<PopoverMenuButtonProps> = ({
  children,
  handleClick,
  hideMenu,
  size = 'lg',
  text,
  placement = 'bottom end',
  className,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useClickOutside(ref, () => {
    setIsOpen(prev => !prev);
  });

  if (hideMenu) {
    return null;
  }

  return (
    <Popover.Root isOpen={isOpen} {...rest}>
      {text ? (
        <Button
          color="secondary"
          size={size}
          className={cn('w-full!', className)}
          variant="outlined"
          data-testid="PopoverButton"
          onPress={() => {
            handleClick?.();
            setIsOpen(prev => !prev);
          }}
        >
          {text}
        </Button>
      ) : (
        <BaseButton
          onPress={() => {
            handleClick?.();
            setIsOpen(prev => !prev);
          }}
          className={cn('p-2!', className)}
          data-testid="PopoverButton"
        >
          <IconDots data-testid="PopoverMenuButton" />
        </BaseButton>
      )}

      <Popover.Panel className="min-w-44 p-0!" placement={placement}>
        <div
          ref={ref}
          role="presentation"
          onClick={() => setIsOpen(false)}
          className="w-full!"
        >
          {children}
        </div>
      </Popover.Panel>
    </Popover.Root>
  );
};

export default PopoverMenuButton;
